"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StandardAutoCloseGameModeTransitionView = void 0;
/**
 * Base class for transitions screens that are have standard button and autoclose.
 *
 * This should be placed in a layer with scaling options listed below to
 * ensure the layout of buttons and auto close text ends up in the correct place.
 *
 * target : ScaleTarget.WINDOW,
 * method : ScaleMethod.FIT,
 * frame : {x : 0, y : 0, width : 720, height : 720},
 * placement : {x : 0.5, y : 0.5}
 *
 * To position game specific graphics use the screenRect property to make sure that relative
 * positions work on all screen sizes. E.g
 *
 * this.myNiceLogo.position.set(this._screenRect.width * 0.5, this._screenRect.height * 0.8);
 *
 *
 * Alternatively make sure you stay inside the "frame" of 720x720 for absolute values, to ensure no graphical elements
 * end up outside of the visible area.
 *
 * Created: 2019-07-30
 * @author jonas
 */
const FontStatics_1 = require("@nolimitcity/slot-launcher/bin/loader/font/FontStatics");
const gsap_1 = require("gsap");
const BaseView_1 = require("../../../core/base/BaseView");
const NineSliceButton_1 = require("../../../core/component/NineSliceButton");
const CountDownTimer_1 = require("../../../core/component/CountDownTimer");
const UserAgent_1 = require("../../../core/useragent/UserAgent");
const FadePlate_1 = require("../../components/FadePlate");
const Translation_1 = require("../../../core/translation/Translation");
class StandardAutoCloseGameModeTransitionView extends BaseView_1.BaseView {
    constructor(layer, autoCloseTimerDuration = 20) {
        super();
        this.MOBILE_SCALE = 1.6;
        this.BUTTON_SIZE = new PIXI.Point(240, 64);
        this.BUTTON_TEXT_STYLE = new PIXI.TextStyle({
            fontFamily: "Open Sans",
            fontStyle: FontStatics_1.FontStyle.ITALIC,
            fontSize: 34,
            fill: "#ffffff",
            fontWeight: FontStatics_1.FontWeight.EXTRA_BOLD
        });
        this._layer = layer;
        this._autoCloseTimerDuration = autoCloseTimerDuration;
    }
    initAnimations() {
        this._deviceScale = UserAgent_1.UserAgent.isMobile ? this.MOBILE_SCALE : 1;
        this._button = this.createButton();
        this._autoCloseTimer = this.createAutoCloseTimer();
        this._mainGraphicsContainer = new PIXI.Container();
        this._mainGraphicsContainer.addChild(this.createBackground());
        this._mainGraphicsContainer.addChild(this.createGameGraphics());
        this._mainGraphicsContainer.addChild(this._button);
        this._mainGraphicsContainer.addChild(this._autoCloseTimer);
        this._mainGraphicsContainer.alpha = 0;
        this._fullScreenFade = new FadePlate_1.FadePlate(0x000000, 1);
        this.addChild(this._fullScreenFade);
        this.addChild(this._mainGraphicsContainer);
    }
    createButton() {
        const text = Translation_1.Translation.translate("CONTINUE");
        const textStyle = this.getScaledButtonTextStyle();
        const button = new NineSliceButton_1.NineSliceButton(() => this.internalCompleteCallback(), ` ${text} `, textStyle);
        button.enabled = false;
        return button;
    }
    getScaledButtonTextStyle() {
        const style = this.BUTTON_TEXT_STYLE;
        if (typeof style.fontSize !== 'number') {
            debugger;
            throw new Error("Error: IntoView.getScaledButtonTextStyle(): please use number fontSize!");
        }
        style.fontSize = style.fontSize * this._deviceScale;
        return style;
    }
    createAutoCloseTimer() {
        const textsSingular = Translation_1.Translation.translate("Closes in ${sec} second").split("${sec}");
        const textsPlural = Translation_1.Translation.translate("Closes in ${sec} seconds").split("${sec}");
        return new CountDownTimer_1.CountDownTimer({
            prefixTextSingular: textsSingular[0],
            prefixTextPlural: textsPlural[0],
            postfixSecondTextSingular: textsSingular[1],
            postfixSecondsTextPlural: textsPlural[1]
        });
    }
    /**
     * Event handler for onStageReSizedEvents.
     *
     * When overriding make sure to run super.onResize, to ensure that button and auto close timer scales and positions correctly.
     *
     * @param data IResizeData
     */
    onResize(data) {
        this._screenRect = new PIXI.Rectangle(0, 0, data.width / this._layer.scale.x, data.height / this._layer.scale.y);
        this.resizeButton();
        this.resizeAutoCloseTimer();
        this._fullScreenFade.resize(0, 0, this._screenRect.width, this._screenRect.height);
        //Negate layer scaling offset.
        this.x = -this._layer.x / this._layer.scale.x;
        this.y = -this._layer.y / this._layer.scale.y;
        this._isResizeDirty = false;
    }
    resizeButton() {
        const buttonSize = new PIXI.Point(this.BUTTON_SIZE.x * this._deviceScale, this.BUTTON_SIZE.y * this._deviceScale);
        this._button.setSize(buttonSize.x, buttonSize.y, this._layer.scale.x);
        this._button.position.set(this._screenRect.width * 0.5, this._screenRect.height * 0.85);
    }
    resizeAutoCloseTimer() {
        this._autoCloseTimer.setScale(this._deviceScale);
        this._autoCloseTimer.position.set(this._button.x, this._button.y + this._button.height * 0.5 + this._autoCloseTimer.height);
    }
    internalCompleteCallback() {
        if (this._completeCallback) {
            this.stopTimerAndDisableButton();
            this._completeCallback();
        }
    }
    startTimerAndEnableButton(callback) {
        this._completeCallback = callback;
        this._autoCloseTimer.start(this._autoCloseTimerDuration, () => this.internalCompleteCallback());
        this._button.enabled = true;
    }
    stopTimerAndDisableButton() {
        this._autoCloseTimer.abort();
        this._button.enabled = false;
    }
    /**
     * This is called just before getStartAnimation to prepare graphics and texts and so on.
     *
     * @param data Server data.
     */
    prepare(data) { }
    /**
     *
     * @param prepareToEnterModeCallback
     * @param completeCallback
     */
    getStartAnimation(prepareToEnterModeCallback, completeCallback) {
        const tl = new gsap_1.TimelineLite();
        tl.add(() => {
            this._layer.addChild(this);
        });
        tl.add(this._fullScreenFade.getFadeIn(1));
        tl.add(() => prepareToEnterModeCallback());
        tl.add([
            new gsap_1.TweenLite(this._mainGraphicsContainer, 0.5, { alpha: 1, ease: gsap_1.Linear.easeNone }),
            this._fullScreenFade.getFadeTo(1, 0.5)
        ]);
        tl.add(() => this.startTimerAndEnableButton(completeCallback));
        return tl;
    }
    getEndAnimation() {
        const tl = new gsap_1.TimelineLite();
        tl.add([
            new gsap_1.TweenLite(this._mainGraphicsContainer, 0.5, { alpha: 0, ease: gsap_1.Linear.easeNone }),
            this._fullScreenFade.getFadeOut(1)
        ]);
        tl.add(() => {
            this._layer.removeChild(this);
        });
        return tl;
    }
}
exports.StandardAutoCloseGameModeTransitionView = StandardAutoCloseGameModeTransitionView;
//# sourceMappingURL=StandardAutoCloseGameModeTransitionView.js.map